import { type JSX } from 'react';
import { generatePath } from 'react-router';

import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { Table } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { TPRM_SERVICE_DASHBOARD } from 'Config/PathTemplates';
import { AssociatedThirdPartyService } from 'Models/TPRM';
import { ColorTheme } from 'Models/Types/GlobalType';

/**
 * @param colorTheme - The type of background that the component will be displayed against. If not supplied, `'light'` will be used.
 */
export interface AssociatedTprmServicesListingProps {
    associatedTprmServices: AssociatedThirdPartyService[];

    colorTheme?: ColorTheme;
}

/** Lists the TPRM third-party services that a use case relies on. Used on the Use Case dashboard; associations are managed on the Manage Use Case page. */
export const AssociatedTprmServicesListing = (props: AssociatedTprmServicesListingProps): JSX.Element => {
    const colorTheme = props.colorTheme ?? 'light';

    if (props.associatedTprmServices.length === 0) {
        return <Text color={colorTheme === 'dark' ? 'white' : undefined}>No third-party services have been mapped to this use case. Third-party services can be mapped by managing the use case.</Text>;
    }

    const columns: ColumnConfig<AssociatedThirdPartyService>[] = [
        {
            type: ColumnType.STRING,
            name: 'Third Party',
            getString: (service) => service.vendor_name,
        },
        {
            type: ColumnType.STRING,
            name: 'Third-Party Service',
            getString: (service) => service.name,
            getOnClickTextAction: (service) => ({
                goToLocation: generatePath(TPRM_SERVICE_DASHBOARD, { third_party_id: service.vendor_id, service_id: service.id }),
            }),
        },
    ];

    return <Table columns={columns} defaultSortColumn="Third Party" filters={[]} getUniqueIdForRow={(service) => service.id} pageVariant={colorTheme === 'light' ? 'standard' : 'dashboard'} rowsData={props.associatedTprmServices} />;
};
